import { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Phone, Calendar, BarChart3, Target, Lightbulb, Handshake, MapPin, Clock, Monitor, XCircle } from "lucide-react";
import SEOHead from "@/components/SEOHead";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const BookingConfirme = () => {
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17857202947/-iolCPbEh94bEIOW_cJC',
        'value': 1.0,
        'currency': 'CAD'
      });
    }
  }, []);

  return (
    <>
      <SEOHead
        title="Diagnostic confirmé"
        description="Confirmation de votre réservation de diagnostic marketing avec Agence 8020."
        canonical="/booking-confirme"
        noIndex={true}
      />

      <div className="min-h-screen bg-background text-foreground">
        {/* Hero Section */}
        <section className="relative px-4 pt-16 pb-12 sm:pt-24 sm:pb-16">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-green-500/10 border border-green-500/20 mb-6"
            >
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-foreground"
            >
              Votre diagnostic est{" "}
              <span className="text-primary">
                confirmé
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-muted-foreground text-base sm:text-lg leading-relaxed"
            >
              Merci pour votre réservation.<br />
              On va analyser votre situation et voir comment générer plus de leads qualifiés grâce au contenu et à la publicité payante.
            </motion.p>
          </div>
        </section>

        {/* À quoi vous attendre */}
        <section className="px-4 pb-12 sm:pb-16">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold">À quoi vous attendre pendant l'appel</h2>
              </div>

              <p className="text-muted-foreground mb-6">Lors de ce diagnostic :</p>

              <div className="space-y-4">
                {[
                  { icon: BarChart3, text: "On analyse votre stratégie actuelle d'acquisition clients" },
                  { icon: Target, text: "On identifie ce qui bloque la génération de leads" },
                  { icon: Lightbulb, text: "On voit ce qui peut être optimisé rapidement" },
                  { icon: Handshake, text: "On valide si un accompagnement fait du sens" },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground/90">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Prépare l'appel */}
        <section className="px-4 pb-12 sm:pb-16">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold">Prépare l'appel</h2>
              </div>

              <p className="text-muted-foreground mb-6">Pour que l'appel soit le plus pertinent possible :</p>

              <div className="space-y-4">
                {[
                  { icon: MapPin, text: "Sois dans un endroit calme" },
                  { icon: Clock, text: "Prévois d'être disponible pendant 30 minutes" },
                  { icon: Monitor, text: "Ayez accès à vos données marketing (si possible)" },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground/90">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Rappel important */}
        <section className="px-4 pb-16 sm:pb-24">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-muted/30 border border-border/50 rounded-2xl p-6 sm:p-8 text-center"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-500/10 mb-4">
                <XCircle className="w-5 h-5 text-orange-500" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Rappel important</h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Cet appel est réservé aux entrepreneurs sérieux.<br />
                Si vous ne pouvez pas être présent ou que votre situation a changé, merci d'annuler à l'avance.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BookingConfirme;